<script>
/**
 * TKP-187 / bulgular md.85 — Görevler modülünün sol menüsü KVKK modülündeki gibi: logolu dikey menü (`vertical-menu2`),
 * etkin kalem vurgulu. Kalemler ve yetki kuralları TKP-118/119/122/164 ile aynı: Pano (organizasyon görevlerini görme),
 * Görevler, Periyodik Görevler, Arşivlenen Görevler, Ayarlar (Görev Yöneticisi / sahip); altında modül geçişi.
 */
import { hasCapability } from "@/route-authorization"; // TKP-119
export default {
  props: {
    type: { type: String, required: true },
    width: { type: String, required: true },
  },
  computed: {
    canEditSettings() {
      return hasCapability("TASK_SETTINGS");
    },
    // TKP-164: pano = organizasyon görevlerini görme yetkisi (analiz sekmesiyle aynı)
    canViewDashboard() {
      return hasCapability("TASK_TASKS__HAS_ORGANIZATION_TASK_VIEW_PERMISSION");
    },
    /** "/tasks" her alt yolun öneki: liste kalemi yalnız listede ve görev ayrıntısında (/tasks/:id) etkin. */
    listActive() {
      const path = (this.$route && this.$route.path) || "";
      return path === "/tasks" || /^\/tasks\/[0-9a-f]{24}$/i.test(path);
    },
  },
  watch: {
    // KVKK yan menüsüyle aynı gövde öznitelikleri (açık yerleşim)
    type: {
      immediate: true,
      handler(value) {
        if (value === "light") {
          document.body.setAttribute("data-topbar", "dark");
          document.body.removeAttribute("data-sidebar");
          document.body.removeAttribute("data-sidebar-size");
          document.body.classList.remove("vertical-collpsed");
        } else {
          document.body.setAttribute("data-sidebar", "dark");
        }
      },
    },
    width: {
      immediate: true,
      handler(value) {
        if (value === "boxed") document.body.setAttribute("data-layout-size", "boxed");
        else {
          document.body.setAttribute("data-layout-mode", "fluid");
          document.body.removeAttribute("data-layout-size");
        }
      },
    },
  },
};
</script>

<template>
  <!-- ========== Left Sidebar Start ========== -->
  <div class="vertical-menu2 d-flex flex-column" style="background-color: rgb(243, 246, 248);" data-test="tasks-side-bar">
    <div class="sidebar--logo">
      <img :src="whiteLabel || require('@/assets/images/logo.png')" height="52" alt="" />
    </div>

    <!-- TKP-164 / tunesoftgorevler s.1: sıra Dashboard, Görevler, Periyodik Görevler, Arşivlenen Görevler, Ayarlar -->
    <router-link v-if="canViewDashboard" tag="a" to="/tasks/dashboard" data-test="tasks-dashboard-link">
      <div class="menu-item">
        <i class="iconoir-stats-report"></i>
        {{ $t("task.dashboard") }}
      </div>
    </router-link>

    <router-link tag="a" to="/tasks" exact :class="{ active: listActive }" data-test="tasks-list-link">
      <div class="menu-item">
        <i class="iconoir-archery"></i>
        {{ $t("task.plural") }}
      </div>
    </router-link>

    <!-- TKP-122: periyodik görev tanımları -->
    <router-link tag="a" to="/tasks/periodic" data-test="tasks-periodic-link">
      <div class="menu-item">
        <i class="iconoir-refresh-double"></i>
        {{ $t("task.periodic.title") }}
      </div>
    </router-link>

    <router-link tag="a" to="/tasks/archived" data-test="tasks-archived-link">
      <div class="menu-item">
        <i class="iconoir-archive"></i>
        {{ $t("task.archived") }}
      </div>
    </router-link>

    <!-- TKP-119: modül ayarları — Görev Yöneticisi ve sahip -->
    <router-link v-if="canEditSettings" tag="a" to="/tasks/settings" data-test="tasks-settings-link">
      <div class="menu-item">
        <i class="iconoir-settings"></i>
        {{ $t("task.settings.title") }}
      </div>
    </router-link>

    <hr />
    <a href="/">
      <div class="menu-item">
        <i class="iconoir-view-grid"></i>
        {{ $t("dashboard.text") }}
      </div>
    </a>
    <a href="/organization">
      <div class="menu-item">
        <i class="iconoir-cube-hole"></i>
        {{ $t("organization.panel") }}
      </div>
    </a>
    <a v-if="hasCurrentOrganizationAccessable('RISK')" href="/risk/dashboard">
      <div class="menu-item">
        <img height="25" src="/icons/risk-active.svg" />
        {{ $t("product.risk.product") }}
      </div>
    </a>
    <a v-if="hasCurrentOrganizationAccessable('GDPR')" href="/gdpr/entities">
      <div class="menu-item">
        <img height="25" src="/icons/gdpr-active.svg" />
        {{ $t("product.gdpr.product") }}
      </div>
    </a>
    <a v-if="hasCurrentOrganizationAccessable('KVKK')" href="/kvkk/entities">
      <div class="menu-item">
        <img height="25" src="/icons/kvkk-active.svg" />
        {{ $t("product.kvkk.product") }}
      </div>
    </a>
    <a v-if="hasCurrentOrganizationAccessable('BGYS')" href="/bgys/roadmap">
      <div class="menu-item">
        <img height="25" src="/icons/bgys-active.svg" />
        {{ $t("product.bgys.product") }}
      </div>
    </a>
    <a v-if="hasCurrentOrganizationAccessable('DDO')" href="/ddo/roadmap">
      <div class="menu-item">
        <img height="25" src="/icons/ddo-active.svg" />
        {{ $t("product.ddo.product") }}
      </div>
    </a>
  </div>
  <!-- Left Sidebar End -->
</template>

<style lang="scss" scoped>
/* KVKK yan menüsüyle aynı kalem biçimi (layouts/kvkk/side-bar.vue) */
.menu-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  color: #535a71;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  max-width: 15rem;
  width: 100%;
  border-radius: 0.625rem;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1rem;
  transition: background-color, color, 0.3s;
  cursor: pointer;

  i {
    position: relative;
    bottom: 1px;
    font-size: 1.5rem;
  }

  &:hover {
    background: #f2e0da;
    color: #ff7c48;

    i {
      color: #ff7c48;
    }
  }
}

.active {
  .menu-item {
    background: #f2e0da;
    color: #ff7c48;

    i {
      color: #ff7c48;
    }
  }
}
</style>
